import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  Radio, PhoneOff, Wifi, WifiOff, Zap, Plus, Users, Folder,
  Mic, ChevronDown, Video, Square, Languages, Send,
  Play, Clock, MessageSquare, Loader2,
  MapPin, Bluetooth, Signal, Settings, Expand,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const WS_BASE = (() => {
  const bUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const p = bUrl.startsWith("https") ? "wss" : "ws";
  return `${p}://${bUrl.replace(/^https?:\/\//, "")}`;
})();

/* ─── 963 Hz CHIRP ─── */
function playChirp(type = "connect") {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "square";
    const t = ctx.currentTime;
    if (type === "send") {
      osc.frequency.setValueAtTime(963, t); osc.frequency.setValueAtTime(1281, t + 0.07);
      gain.gain.setValueAtTime(0.14, t); gain.gain.linearRampToValueAtTime(0, t + 0.14);
      osc.start(t); osc.stop(t + 0.16);
    } else if (type === "receive") {
      osc.frequency.setValueAtTime(1281, t); osc.frequency.setValueAtTime(963, t + 0.07);
      gain.gain.setValueAtTime(0.12, t); gain.gain.linearRampToValueAtTime(0, t + 0.14);
      osc.start(t); osc.stop(t + 0.16);
    } else if (type === "grant") {
      osc.frequency.setValueAtTime(770, t); osc.frequency.linearRampToValueAtTime(1445, t + 0.12);
      gain.gain.setValueAtTime(0.12, t); gain.gain.linearRampToValueAtTime(0, t + 0.14);
      osc.start(t); osc.stop(t + 0.16);
    } else {
      osc.frequency.setValueAtTime(963, t); osc.frequency.linearRampToValueAtTime(481, t + 0.12);
      gain.gain.setValueAtTime(0.1, t); gain.gain.linearRampToValueAtTime(0, t + 0.14);
      osc.start(t); osc.stop(t + 0.16);
    }
  } catch { /* */ }
}

function playFrequencyCode(hz) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sine"; osc.frequency.setValueAtTime(hz, ctx.currentTime);
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.42);
  } catch { /* */ }
}

const FREQ_CODES = [
  { hz: 174, label: "174", tag: "LO", color: "#FF0000" },
  { hz: 285, label: "285", tag: "RES", color: "#FF2200" },
  { hz: 396, label: "396", tag: "BASE", color: "#FF4400" },
  { hz: 417, label: "417", tag: "MID", color: "#FF6600" },
  { hz: 432, label: "432", tag: "REF", color: "#FF8800" },
  { hz: 528, label: "528", tag: "CORE", color: "#FFAA00" },
  { hz: 639, label: "639", tag: "LINK", color: "#FFCC00" },
  { hz: 741, label: "741", tag: "HI", color: "#CCFF00" },
  { hz: 852, label: "852", tag: "PEAK", color: "#88FF00" },
  { hz: 963, label: "963", tag: "TOP", color: "#FF0000" },
];

const CLIP_DURATIONS = [
  { seconds: 13, label: "13s", tag: "Short" },
  { seconds: 33, label: "33s", tag: "Standard" },
  { seconds: 96, label: "96s", tag: "Extended" },
];

const CONN_MODES = [
  { id: "wifi", label: "Wi-Fi", icon: Wifi, color: "#FF2040", desc: "Connect through local networks. Ideal for indoor areas with limited cellular." },
  { id: "lora", label: "LoRa", icon: Signal, color: "#FF69B4", desc: "Long-range mesh radio. 10+ mile range. Peer-to-peer without infrastructure." },
  { id: "ptt", label: "PTT", icon: Radio, color: "#39FF14", desc: "Push-to-Talk over cellular. Nationwide coverage. 963 Hz chirp protocol." },
];

/* ─── WAVEFORM ─── */
function WaveformBar({ active, level = 0, connColor = "#FF0000" }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width, h = canvas.height;
    const draw = () => {
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, w, h);
      if (active) {
        const bars = 28;
        const barW = (w - (bars - 1) * 2) / bars;
        for (let i = 0; i < bars; i++) {
          const amp = Math.random() * 0.7 + 0.3;
          const barH = amp * h * (0.3 + level * 0.7);
          const x = i * (barW + 2), y = (h - barH) / 2;
          const a = 0.4 + amp * 0.6;
          ctx.fillStyle = connColor + Math.round(a * 255).toString(16).padStart(2, "0");
          ctx.fillRect(x, y, barW, barH);
        }
      } else {
        ctx.strokeStyle = connColor + "33"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2); ctx.stroke();
      }
      frameRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frameRef.current);
  }, [active, level, connColor]);
  return <canvas ref={canvasRef} width={200} height={36} className="w-full h-9" data-testid="codes-waveform" />;
}

/* ─── MAIN ─── */
export default function CodesDirectConnect({ compact = false }) {
  const [mirrorsId] = useState(() => localStorage.getItem("mirrors_id") || "");
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("idle");
  const [holder, setHolder] = useState(null);
  const [queuePos, setQueuePos] = useState(0);
  const [talkTimer, setTalkTimer] = useState(0);
  const [sentCodes, setSentCodes] = useState([]);
  const [receivedCodes, setReceivedCodes] = useState([]);
  const [showCodes, setShowCodes] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);

  // Talkgroup
  const [talkgroups, setTalkgroups] = useState([]);
  const [activeTG, setActiveTG] = useState(null);
  const [showTGPanel, setShowTGPanel] = useState(false);
  const [newTGName, setNewTGName] = useState("");
  const [presence, setPresence] = useState("AVAILABLE");

  // Mode & connectivity
  const [mode, setMode] = useState("ptt"); // ptt | clip
  const [connMode, setConnMode] = useState("ptt");
  const [targetLang, setTargetLang] = useState("English");
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [showSpecs, setShowSpecs] = useState(false);

  // Recording
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [lastRecording, setLastRecording] = useState(null);

  // Clip
  const [clipDuration, setClipDuration] = useState(13);
  const [isRecordingClip, setIsRecordingClip] = useState(false);
  const [clipCountdown, setClipCountdown] = useState(0);
  const [clipPreview, setClipPreview] = useState(null);
  const liveVideoRef = useRef(null);
  const clipTimerRef = useRef(null);
  const clipStreamRef = useRef(null);

  // Feed
  const [messages, setMessages] = useState([]);
  const [showFeed, setShowFeed] = useState(false);
  const feedPollRef = useRef(null);
  const [textInput, setTextInput] = useState("");

  const channelId = activeTG ? activeTG.id : "PUBLIC";
  const activeConn = CONN_MODES.find(c => c.id === connMode) || CONN_MODES[2];

  // Load talkgroups
  useEffect(() => {
    if (!mirrorsId) return;
    fetch(`${API}/chirp/talkgroups?member_id=${encodeURIComponent(mirrorsId)}`).then(r => r.json()).then(setTalkgroups).catch(() => {});
  }, [mirrorsId]);

  // Poll messages
  useEffect(() => {
    const poll = () => { fetch(`${API}/codes/messages/${channelId}?limit=30`).then(r => r.json()).then(setMessages).catch(() => {}); };
    poll();
    feedPollRef.current = setInterval(poll, 5000);
    return () => clearInterval(feedPollRef.current);
  }, [channelId]);

  // WebSocket
  useEffect(() => {
    if (!mirrorsId) return;
    const ws = new WebSocket(`${WS_BASE}/api/ws/chirp/${encodeURIComponent(mirrorsId)}`);
    wsRef.current = ws;
    ws.onopen = () => { setConnected(true); playChirp("send"); };
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        const type = (msg.type || "").toLowerCase();
        const data = msg.data || {};
        if (type === "floor_grant") { if (data.holder === mirrorsId) { setStatus("granted"); setHolder(mirrorsId); playChirp("grant"); } else setHolder(data.holder); }
        else if (type === "floor_release") { if (data.holder === mirrorsId) setStatus("idle"); setHolder(null); }
        else if (type === "floor_busy") { setStatus("busy"); setQueuePos(data.queuePosition || 0); playChirp("receive"); }
        else if (type === "freq_code") { playFrequencyCode(data.hz); playChirp("receive"); setReceivedCodes(prev => [...prev.slice(-9), { hz: data.hz, from: msg.from || "unknown", ts: Date.now() }]); }
      } catch { /* */ }
    };
    return () => { try { ws.close(); } catch {} wsRef.current = null; };
  }, [mirrorsId]);

  const send = useCallback((type, data) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ type, data })); return true;
  }, []);

  // PTT mic + recording
  useEffect(() => {
    if (status === "granted") {
      setTalkTimer(0);
      timerRef.current = setInterval(() => setTalkTimer(t => t + 0.1), 100);
      navigator.mediaDevices?.getUserMedia({ audio: true }).then(stream => {
        streamRef.current = stream;
        const actx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = actx;
        const src = actx.createMediaStreamSource(stream);
        const analyser = actx.createAnalyser(); analyser.fftSize = 256;
        src.connect(analyser); analyserRef.current = analyser;
        recordedChunksRef.current = [];
        const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4" });
        mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
        mr.start(100); mediaRecorderRef.current = mr;
        const poll = () => { if (!analyserRef.current) return; const d = new Uint8Array(analyser.frequencyBinCount); analyser.getByteFrequencyData(d); setAudioLevel(d.reduce((a, b) => a + b, 0) / d.length / 255); requestAnimationFrame(poll); };
        poll();
      }).catch(() => {});
    } else {
      clearInterval(timerRef.current); setTalkTimer(0); setAudioLevel(0);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop(); mediaRecorderRef.current = null;
        if (recordedChunksRef.current.length > 0) {
          const blob = new Blob(recordedChunksRef.current, { type: "audio/webm" });
          setLastRecording(blob);
          postVoiceMessage(blob);
        }
      }
      streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
      audioCtxRef.current?.close().catch(() => {}); audioCtxRef.current = null; analyserRef.current = null;
    }
    return () => { clearInterval(timerRef.current); streamRef.current?.getTracks().forEach(t => t.stop()); };
  }, [status]);

  const postVoiceMessage = async () => {
    try {
      await fetch(`${API}/codes/messages`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel_id: channelId, sender_id: mirrorsId, sender_name: mirrorsId, msg_type: "voice", content: "[Voice Transmission]", target_language: targetLang }),
      });
      fetch(`${API}/codes/messages/${channelId}?limit=30`).then(r => r.json()).then(setMessages).catch(() => {});
    } catch { /* */ }
  };

  const talkDown = useCallback(() => {
    if (!mirrorsId) { toast.message("Create a MIRRORS ID first"); return; }
    playChirp("send"); setStatus("requesting");
    const sent = send("floor_request", { channelId });
    if (!sent) setTimeout(() => { setStatus("granted"); setHolder(mirrorsId); playChirp("grant"); }, 300);
  }, [mirrorsId, channelId, send]);

  const talkUp = useCallback(() => { setStatus("idle"); send("floor_release", { channelId }); playChirp("receive"); }, [channelId, send]);

  const sendFreqCode = code => {
    playFrequencyCode(code.hz); playChirp("send");
    send("freq_code", { hz: code.hz, channelId, tag: code.tag });
    setSentCodes(prev => [...prev.slice(-9), { hz: code.hz, ts: Date.now() }]);
    fetch(`${API}/codes/messages`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel_id: channelId, sender_id: mirrorsId, sender_name: mirrorsId, msg_type: "freq_code", content: `${code.hz} Hz ${code.tag}`, metadata: { hz: code.hz, tag: code.tag } }),
    }).then(() => fetch(`${API}/codes/messages/${channelId}?limit=30`).then(r => r.json()).then(setMessages)).catch(() => {});
  };

  const sendTextMessage = async () => {
    if (!textInput.trim() || !mirrorsId) return;
    const text = textInput.trim(); setTextInput("");
    let translation = "";
    if (targetLang && targetLang !== "English") {
      try { const res = await fetch(`${API}/codes/translate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, target_language: targetLang }) }); const d = await res.json(); translation = d.translation || ""; } catch { /* */ }
    }
    await fetch(`${API}/codes/messages`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel_id: channelId, sender_id: mirrorsId, sender_name: mirrorsId, msg_type: "text", content: text, translation, target_language: targetLang }),
    }).catch(() => {});
    fetch(`${API}/codes/messages/${channelId}?limit=30`).then(r => r.json()).then(setMessages).catch(() => {});
  };

  // Clips
  const startClip = async () => {
    if (!mirrorsId) { toast.message("Create a MIRRORS ID first"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 480, height: 640 }, audio: true });
      clipStreamRef.current = stream;
      if (liveVideoRef.current) { liveVideoRef.current.srcObject = stream; liveVideoRef.current.play().catch(() => {}); }
      recordedChunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.onstop = () => { const blob = new Blob(recordedChunksRef.current, { type: mime }); setClipPreview({ blob, url: URL.createObjectURL(blob), duration: clipDuration }); clipStreamRef.current?.getTracks().forEach(t => t.stop()); clipStreamRef.current = null; };
      mr.start(100); mediaRecorderRef.current = mr;
      setIsRecordingClip(true); setClipCountdown(clipDuration); playChirp("send");
      let rem = clipDuration;
      clipTimerRef.current = setInterval(() => { rem -= 1; setClipCountdown(rem); if (rem <= 0) { clearInterval(clipTimerRef.current); stopClip(); } }, 1000);
    } catch { toast.error("Camera access required"); }
  };
  const stopClip = () => { clearInterval(clipTimerRef.current); if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop(); setIsRecordingClip(false); setClipCountdown(0); playChirp("receive"); };
  const sendClip = async () => { if (!clipPreview) return; toast.message("Clip sent"); await fetch(`${API}/codes/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ channel_id: channelId, sender_id: mirrorsId, sender_name: mirrorsId, msg_type: "clip", content: `Video Clip (${clipPreview.duration}s)`, clip_duration: clipPreview.duration }) }).catch(() => {}); fetch(`${API}/codes/messages/${channelId}?limit=30`).then(r => r.json()).then(setMessages).catch(() => {}); setClipPreview(null); };
  const downloadClip = () => { if (!clipPreview) return; const a = document.createElement("a"); a.href = clipPreview.url; a.download = `GODMODE_Clip_${clipPreview.duration}s_${Date.now()}.webm`; a.click(); };

  const createTalkgroup = async () => {
    if (!newTGName.trim() || !mirrorsId) return;
    try { const res = await fetch(`${API}/chirp/talkgroups`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: newTGName.trim(), owner_id: mirrorsId }) }); const tg = await res.json(); setTalkgroups(prev => [tg, ...prev]); setNewTGName(""); setActiveTG(tg); toast.success(`Talkgroup ${tg.name} created`); } catch { toast.error("Failed to create talkgroup"); }
  };

  const talkBtnColor =
    status === "granted" ? `shadow-[0_0_40px_${activeConn.color}80]`
    : status === "requesting" ? "animate-pulse"
    : "";

  return (
    <div className="space-y-3" data-testid="codes-direct-connect">

      {/* ─── TOP BAR ─── */}
      <div className="flex items-center justify-between gap-2" data-testid="codes-status-bar">
        <div className="flex items-center gap-3">
          {connected ? <Wifi className="w-3 h-3" style={{ color: activeConn.color }} /> : <WifiOff className="w-3 h-3 text-[#444]" />}
          <span className="text-[9px] font-mono tracking-[0.2em] uppercase" style={{ color: connected ? activeConn.color : "#444" }}>
            {connected ? connMode.toUpperCase() : "OFFLINE"}
          </span>
          <div className="flex items-center gap-1 ml-1">
            {["AVAILABLE", "BUSY", "DND"].map(p => (
              <button key={p} onClick={() => { setPresence(p); send("presence_update", { presence: p }); }}
                className={`w-2 h-2 rounded-full transition-all ${presence === p ? "ring-1 ring-offset-1 ring-offset-black" : "opacity-40"}`}
                style={{ backgroundColor: p === "AVAILABLE" ? activeConn.color : p === "BUSY" ? "#FF6600" : "#666" }}
                title={p} data-testid={`codes-presence-${p.toLowerCase()}`} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSpecs(v => !v)}
            className="text-[8px] font-mono tracking-wider border px-1.5 py-0.5 transition-all"
            style={{ color: `${activeConn.color}50`, borderColor: `${activeConn.color}20` }}
            data-testid="codes-specs-toggle">SPECS</button>
          <div className="text-[9px] font-mono tracking-wider" style={{ color: `${activeConn.color}80` }} data-testid="codes-channel-label">
            CH: {activeTG ? activeTG.name : "PUBLIC"}
          </div>
        </div>
      </div>

      {/* ─── CONNECTIVITY MODE SELECTOR ─── */}
      <div className="flex gap-1" data-testid="conn-mode-selector">
        {CONN_MODES.map(cm => {
          const Icon = cm.icon;
          const active = connMode === cm.id;
          return (
            <button key={cm.id} onClick={() => { setConnMode(cm.id); playChirp("send"); }}
              className="flex-1 flex flex-col items-center gap-1 py-2 border transition-all"
              style={{
                borderColor: active ? cm.color : `${cm.color}20`,
                background: active ? `radial-gradient(circle at 50% 30%, ${cm.color}18 0%, rgba(0,0,0,0.95) 70%)` : "rgba(0,0,0,0.6)",
                boxShadow: active ? `0 0 12px ${cm.color}30, inset 0 0 16px ${cm.color}08` : "none",
              }}
              data-testid={`conn-${cm.id}`}>
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all`}
                style={{ borderColor: active ? cm.color : `${cm.color}30` }}>
                <div className="w-1.5 h-1.5 rounded-full transition-all"
                  style={{ backgroundColor: active ? cm.color : `${cm.color}15`, boxShadow: active ? `0 0 6px ${cm.color}` : "none" }} />
              </div>
              <Icon className="w-3 h-3" style={{ color: active ? cm.color : `${cm.color}30` }} />
              <span className="text-[8px] font-mono tracking-wider" style={{ color: active ? cm.color : `${cm.color}30` }}>{cm.label}</span>
            </button>
          );
        })}
      </div>

      {/* ─── SPECS PANEL ─── */}
      {showSpecs && (
        <div className="border bg-[#050000]/80 p-3 space-y-2" style={{ borderColor: `${activeConn.color}15` }} data-testid="codes-specs-panel">
          <div className="text-[9px] font-mono tracking-[0.2em] uppercase" style={{ color: `${activeConn.color}60` }}>CODES by GODMODE LLC — {activeConn.label}</div>
          <div className="text-[8px] font-mono leading-relaxed" style={{ color: `${activeConn.color}50` }}>{activeConn.desc}</div>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { icon: Signal, label: "PoC Cellular", sub: "Nationwide coverage" },
              { icon: Expand, label: "8→96 Channels", sub: "Expandable capacity" },
              { icon: MapPin, label: "GPS Tracking", sub: "Real-time location" },
              { icon: Bluetooth, label: "Bluetooth", sub: "Hands-free accessories" },
              { icon: Settings, label: "OTA Updates", sub: "Remote programming" },
              { icon: Zap, label: "963 Hz Chirp", sub: "Solfeggio protocol" },
            ].map(f => {
              const FIcon = f.icon;
              return (
                <div key={f.label} className="flex items-start gap-1.5 p-1.5 border bg-black/40" style={{ borderColor: `${activeConn.color}10` }}>
                  <FIcon className="w-3 h-3 shrink-0 mt-0.5" style={{ color: `${activeConn.color}50` }} />
                  <div>
                    <div className="text-[8px] font-mono font-bold" style={{ color: `${activeConn.color}70` }}>{f.label}</div>
                    <div className="text-[7px] font-mono" style={{ color: `${activeConn.color}25` }}>{f.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── MODE TABS ─── */}
      <div className="flex gap-1" data-testid="codes-mode-tabs">
        <button onClick={() => setMode("ptt")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-mono tracking-[0.15em] uppercase border transition-all ${
            mode === "ptt" ? `bg-opacity-10 text-opacity-100` : "border-[#222] text-[#555] hover:text-[#FF0000]/60"}`}
          style={mode === "ptt" ? { borderColor: `${activeConn.color}50`, backgroundColor: `${activeConn.color}10`, color: activeConn.color } : {}}
          data-testid="mode-ptt"><Mic className="w-3 h-3" /> DIRECT CONNECT</button>
        <button onClick={() => setMode("clip")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-mono tracking-[0.15em] uppercase border transition-all ${
            mode === "clip" ? `bg-opacity-10 text-opacity-100` : "border-[#222] text-[#555] hover:text-[#FF0000]/60"}`}
          style={mode === "clip" ? { borderColor: `${activeConn.color}50`, backgroundColor: `${activeConn.color}10`, color: activeConn.color } : {}}
          data-testid="mode-clip"><Video className="w-3 h-3" /> VIDEO CLIP</button>
      </div>

      {/* ─── LANGUAGE ─── */}
      <div className="flex items-center gap-2">
        <button onClick={() => setShowLangPicker(v => !v)}
          className="flex items-center gap-1.5 px-2 py-1 border text-[9px] font-mono tracking-wider transition-all"
          style={{ borderColor: `${activeConn.color}20`, color: `${activeConn.color}60` }}
          data-testid="lang-picker-toggle">
          <Languages className="w-3 h-3" /> {targetLang}
          <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showLangPicker ? "rotate-180" : ""}`} />
        </button>
      </div>
      {showLangPicker && (
        <div className="grid grid-cols-4 gap-1 max-h-28 overflow-y-auto" data-testid="lang-picker-grid">
          {["English","Spanish","French","German","Portuguese","Italian","Japanese","Korean","Chinese","Arabic","Hindi","Russian","Dutch","Swedish","Turkish","Polish"].map(lang => (
            <button key={lang} onClick={() => { setTargetLang(lang); setShowLangPicker(false); }}
              className={`px-1.5 py-1 text-[8px] font-mono border transition-all ${targetLang === lang ? "border-[#FF0000] bg-[#FF0000]/10 text-[#FF0000]" : "border-[#222] text-[#555]"}`}
              data-testid={`lang-${lang.toLowerCase()}`}>{lang}</button>
          ))}
        </div>
      )}

      {/* ═══ PTT MODE ═══ */}
      {mode === "ptt" && (
        <>
          {/* Talkgroups */}
          <div>
            <button onClick={() => setShowTGPanel(v => !v)} className="flex items-center gap-2 w-full text-left" data-testid="codes-tg-toggle">
              <Folder className="w-3 h-3" style={{ color: `${activeConn.color}60` }} />
              <span className="text-[10px] font-mono tracking-[0.15em] uppercase flex-1" style={{ color: `${activeConn.color}60` }}>Talkgroups</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showTGPanel ? "rotate-180" : ""}`} style={{ color: `${activeConn.color}40` }} />
            </button>
            {showTGPanel && (
              <div className="space-y-2 pl-1 mt-2" data-testid="codes-tg-panel">
                <button onClick={() => setActiveTG(null)}
                  className={`flex items-center gap-2 w-full px-3 py-2 border text-xs font-mono transition-all`}
                  style={!activeTG ? { borderColor: activeConn.color, backgroundColor: `${activeConn.color}10`, color: activeConn.color } : { borderColor: "#222", color: "#666" }}
                  data-testid="codes-tg-public">
                  <Radio className="w-3 h-3" /><span className="flex-1 text-left tracking-wider">PUBLIC</span><span className="text-[8px] opacity-50">OPEN</span>
                </button>
                {talkgroups.map(tg => (
                  <button key={tg.id} onClick={() => setActiveTG(tg)}
                    className="flex items-center gap-2 w-full px-3 py-2 border text-xs font-mono transition-all"
                    style={activeTG?.id === tg.id ? { borderColor: activeConn.color, backgroundColor: `${activeConn.color}10`, color: activeConn.color } : { borderColor: "#222", color: "#666" }}
                    data-testid={`codes-tg-${tg.id}`}>
                    <Users className="w-3 h-3" /><span className="flex-1 text-left tracking-wider">{tg.name}</span>
                  </button>
                ))}
                <div className="flex items-center gap-1.5 mt-1" data-testid="codes-tg-create">
                  <Input value={newTGName} onChange={e => setNewTGName(e.target.value)} placeholder="New talkgroup..."
                    className="h-7 text-xs font-mono bg-black border-[#333] placeholder:text-[#333] flex-1"
                    style={{ color: activeConn.color }}
                    onKeyDown={e => e.key === "Enter" && createTalkgroup()} data-testid="codes-tg-name-input" />
                  <Button size="sm" className="h-7 px-2 border-0" style={{ backgroundColor: `${activeConn.color}20`, color: activeConn.color }}
                    onClick={createTalkgroup} data-testid="codes-tg-create-btn"><Plus className="w-3 h-3" /></Button>
                </div>
              </div>
            )}
          </div>

          {/* Waveform */}
          <WaveformBar active={status === "granted"} level={audioLevel} connColor={activeConn.color} />

          {/* Talk Button */}
          <div className="flex flex-col items-center gap-2 py-2" data-testid="codes-talk-area">
            <button
              className={`${compact ? "w-24 h-24" : "w-32 h-32"} rounded-full flex items-center justify-center transition-all duration-100 select-none touch-none ${talkBtnColor}`}
              style={{
                backgroundColor: status === "granted" ? activeConn.color : "#0a0000",
                border: status === "granted" ? "none" : `2px solid ${activeConn.color}40`,
              }}
              onPointerDown={e => { e.preventDefault(); talkDown(); }}
              onPointerUp={e => { e.preventDefault(); if (status !== "idle") talkUp(); }}
              onPointerLeave={() => { if (status !== "idle") talkUp(); }}
              onContextMenu={e => e.preventDefault()}
              data-testid="codes-talk-button">
              <div className="flex flex-col items-center gap-1">
                {status === "granted"
                  ? <Mic className={`${compact ? "w-7 h-7" : "w-9 h-9"} text-black`} />
                  : <Radio className={`${compact ? "w-7 h-7" : "w-9 h-9"}`} style={{ color: activeConn.color }} />}
                <span className={`text-[10px] font-mono tracking-[0.25em] uppercase font-bold`}
                  style={{ color: status === "granted" ? "black" : `${activeConn.color}CC` }}>
                  {status === "granted" ? "LIVE" : status === "requesting" ? "..." : status === "busy" ? "WAIT" : connMode.toUpperCase()}
                </span>
              </div>
            </button>
            <div className="text-center h-5" data-testid="codes-status-line">
              {status === "granted" && <div className="text-[11px] font-mono tracking-wider animate-pulse" style={{ color: activeConn.color }}>TX {talkTimer.toFixed(1)}s — {connMode.toUpperCase()}</div>}
              {status === "requesting" && <div className="text-[11px] font-mono tracking-wider" style={{ color: `${activeConn.color}80` }}>REQUESTING FLOOR...</div>}
              {status === "busy" && <div className="text-[#666] text-[11px] font-mono">BUSY — #{queuePos}</div>}
              {status === "idle" && holder && holder !== mirrorsId && <div className="text-[#444] text-[11px] font-mono">{holder} ON AIR</div>}
            </div>
            {(status === "granted" || status === "requesting") && (
              <Button variant="outline" size="sm" className="font-mono text-[10px] h-7" style={{ borderColor: `${activeConn.color}30`, color: activeConn.color }}
                onClick={talkUp} data-testid="codes-end-button"><PhoneOff className="w-3 h-3 mr-1" /> END</Button>
            )}
          </div>

          {/* Text input */}
          <div className="flex items-center gap-1.5 border-t pt-2" style={{ borderColor: `${activeConn.color}10` }} data-testid="text-input-section">
            <Input value={textInput} onChange={e => setTextInput(e.target.value)} placeholder="Type a message..."
              className="h-8 text-xs font-mono bg-black border-[#333] placeholder:text-[#333] flex-1"
              style={{ color: activeConn.color }}
              onKeyDown={e => e.key === "Enter" && sendTextMessage()} data-testid="codes-text-input" />
            <Button size="sm" className="h-8 px-3 border-0" style={{ backgroundColor: `${activeConn.color}20`, color: activeConn.color }}
              onClick={sendTextMessage} disabled={!textInput.trim()} data-testid="codes-text-send"><Send className="w-3.5 h-3.5" /></Button>
          </div>
        </>
      )}

      {/* ═══ CLIP MODE ═══ */}
      {mode === "clip" && (
        <div className="space-y-3" data-testid="clip-mode">
          <div className="space-y-1">
            <div className="text-[9px] font-mono tracking-[0.15em] uppercase flex items-center gap-1.5" style={{ color: `${activeConn.color}40` }}>
              <Clock className="w-3 h-3" /> Clip Duration
            </div>
            <div className="flex gap-1.5">
              {CLIP_DURATIONS.map(d => (
                <button key={d.seconds} onClick={() => !isRecordingClip && setClipDuration(d.seconds)}
                  className="flex-1 flex flex-col items-center gap-0.5 py-2 border font-mono transition-all"
                  style={clipDuration === d.seconds ? { borderColor: activeConn.color, backgroundColor: `${activeConn.color}15`, color: activeConn.color } : { borderColor: "#222", color: "#555" }}
                  data-testid={`clip-dur-${d.seconds}`}>
                  <span className="text-sm font-bold">{d.label}</span>
                  <span className="text-[7px] tracking-wider opacity-60">{d.tag}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="relative bg-black border overflow-hidden" style={{ borderColor: `${activeConn.color}20`, aspectRatio: "3/4", maxHeight: 320 }} data-testid="clip-viewfinder">
            {isRecordingClip && (
              <>
                <video ref={liveVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-6xl font-mono font-bold opacity-50" style={{ color: activeConn.color }}>{clipCountdown}</div>
                </div>
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: activeConn.color }} />
                  <span className="text-[10px] font-mono tracking-wider" style={{ color: activeConn.color }}>REC {clipDuration - clipCountdown}s / {clipDuration}s</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: `${activeConn.color}20` }}>
                  <div className="h-full transition-all" style={{ width: `${((clipDuration - clipCountdown) / clipDuration) * 100}%`, backgroundColor: activeConn.color }} />
                </div>
              </>
            )}
            {!isRecordingClip && !clipPreview && (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                <Video className="w-10 h-10" style={{ color: `${activeConn.color}20` }} />
                <span className="text-[10px] font-mono tracking-wider" style={{ color: `${activeConn.color}30` }}>TAP RECORD TO START</span>
              </div>
            )}
            {!isRecordingClip && clipPreview && (
              <video src={clipPreview.url} className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} controls playsInline data-testid="clip-preview-video" />
            )}
          </div>
          <div className="flex items-center justify-center gap-3">
            {!isRecordingClip && !clipPreview && (
              <button onClick={startClip} className="w-16 h-16 rounded-full border-2 flex items-center justify-center transition-all"
                style={{ borderColor: `${activeConn.color}50`, backgroundColor: "#0a0000" }}
                data-testid="clip-record-btn"><div className="w-6 h-6 rounded-full" style={{ backgroundColor: activeConn.color }} /></button>
            )}
            {isRecordingClip && (
              <button onClick={stopClip} className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse"
                style={{ backgroundColor: activeConn.color }} data-testid="clip-stop-btn"><Square className="w-6 h-6 text-black fill-black" /></button>
            )}
            {clipPreview && (
              <>
                <Button variant="outline" className="font-mono text-[10px]" style={{ borderColor: `${activeConn.color}40`, color: activeConn.color }}
                  onClick={() => setClipPreview(null)} data-testid="clip-retake-btn">RETAKE</Button>
                <Button className="font-mono text-[10px]" style={{ backgroundColor: `${activeConn.color}20`, color: activeConn.color }}
                  onClick={downloadClip} data-testid="clip-download-btn">SAVE</Button>
                <Button className="font-mono text-[10px] font-bold" style={{ backgroundColor: activeConn.color, color: "black" }}
                  onClick={sendClip} data-testid="clip-send-btn"><Send className="w-3 h-3 mr-1" /> SEND</Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── FREQUENCY CODES ─── */}
      <div className="border-t pt-2" style={{ borderColor: `${activeConn.color}10` }} data-testid="freq-codes-section">
        <button onClick={() => setShowCodes(v => !v)} className="flex items-center gap-2 w-full text-left" data-testid="freq-codes-toggle">
          <Zap className="w-3 h-3" style={{ color: `${activeConn.color}60` }} />
          <span className="text-[10px] font-mono tracking-[0.15em] uppercase flex-1" style={{ color: `${activeConn.color}60` }}>Frequency CODES</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${showCodes ? "rotate-180" : ""}`} style={{ color: `${activeConn.color}40` }} />
        </button>
        {showCodes && (
          <div className="mt-2 space-y-2" data-testid="freq-codes-picker">
            <div className="grid grid-cols-5 gap-1.5">
              {FREQ_CODES.map(code => (
                <button key={code.hz} onClick={() => sendFreqCode(code)}
                  className="group/code flex flex-col items-center gap-0.5 py-2 px-1 border border-[#222] bg-black/60 hover:border-[#FF0000]/40 transition-all active:scale-90"
                  data-testid={`freq-code-${code.hz}`}>
                  <span className="text-xs font-mono font-bold" style={{ color: code.color }}>{code.label}</span>
                  <span className="text-[7px] font-mono tracking-wider text-[#555]">{code.tag}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── MESSAGE FEED ─── */}
      <div className="border-t pt-2" style={{ borderColor: `${activeConn.color}10` }} data-testid="message-feed-section">
        <button onClick={() => setShowFeed(v => !v)} className="flex items-center gap-2 w-full text-left" data-testid="feed-toggle">
          <MessageSquare className="w-3 h-3" style={{ color: `${activeConn.color}60` }} />
          <span className="text-[10px] font-mono tracking-[0.15em] uppercase flex-1" style={{ color: `${activeConn.color}60` }}>
            Channel Feed {messages.length > 0 ? `(${messages.length})` : ""}
          </span>
          <ChevronDown className={`w-3 h-3 transition-transform ${showFeed ? "rotate-180" : ""}`} style={{ color: `${activeConn.color}40` }} />
        </button>
        {showFeed && (
          <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto" data-testid="message-feed-list">
            {messages.length === 0 && <div className="text-[9px] font-mono text-[#333] text-center py-4">No transmissions yet</div>}
            {messages.map(msg => (
              <div key={msg.id} className="px-2 py-1.5 border bg-black/40" style={{ borderColor: `${activeConn.color}08` }} data-testid={`feed-msg-${msg.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[8px] font-mono" style={{ color: `${activeConn.color}40` }}>{msg.sender_name}</span>
                  <span className="text-[7px] font-mono text-[#333]">{new Date(msg.created_at).toLocaleTimeString()}</span>
                  {msg.msg_type === "voice" && <Mic className="w-2.5 h-2.5" style={{ color: `${activeConn.color}30` }} />}
                  {msg.msg_type === "clip" && <Video className="w-2.5 h-2.5" style={{ color: `${activeConn.color}30` }} />}
                  {msg.msg_type === "freq_code" && <Zap className="w-2.5 h-2.5" style={{ color: `${activeConn.color}30` }} />}
                </div>
                <div className="text-[10px] font-mono mt-0.5" style={{ color: `${activeConn.color}80` }}>{msg.content}</div>
                {msg.translation && (
                  <div className="text-[10px] font-mono mt-0.5 pl-2 border-l border-[#FFAA00]/20 text-[#FFAA00]/70">
                    <Languages className="w-2.5 h-2.5 inline mr-1 opacity-50" />{msg.translation}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── FOOTER ─── */}
      {!compact && (
        <div className="flex items-center justify-between text-[9px] font-mono text-[#333] border-t pt-2" style={{ borderColor: `${activeConn.color}10` }} data-testid="codes-floor-info">
          <span>FLOOR: {holder ? "HELD" : "OPEN"}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1" style={{ color: connMode === "ptt" ? `${activeConn.color}60` : "#333" }}><Radio className="w-2.5 h-2.5" /> PoC</span>
            <span className="flex items-center gap-1" style={{ color: connMode === "wifi" ? `${activeConn.color}60` : "#333" }}><Wifi className="w-2.5 h-2.5" /> Wi-Fi</span>
            <span className="flex items-center gap-1" style={{ color: connMode === "lora" ? `${activeConn.color}60` : "#333" }}><Signal className="w-2.5 h-2.5" /> LoRa</span>
          </div>
          <span>8→96 CH</span>
        </div>
      )}
    </div>
  );
}
